"use client";

import { Link } from "@/components/ui/Link";
import { usePathname } from "next/navigation";
import {
  locales,
  localeLabels,
  localeNames,
  switchLocalePath,
  type Locale,
} from "@/lib/i18n";
import { useLocale } from "@/components/i18n/LocaleProvider";

type LanguageSwitcherProps = {
  className?: string;
};

export function LanguageSwitcher({ className = "" }: LanguageSwitcherProps) {
  const locale = useLocale();
  const pathname = usePathname() || `/${locale}/`;

  return (
    <div
      className={`inline-flex items-center gap-0.5 text-xs font-medium tracking-wide ${className}`}
      role="navigation"
      aria-label="Language"
    >
      {locales.map((code, index) => {
        const active = code === locale;
        const href = switchLocalePath(pathname, code);
        return (
          <span key={code} className="inline-flex items-center">
            {index > 0 ? (
              <span className="mx-1.5 text-muted/40" aria-hidden="true">
                /
              </span>
            ) : null}
            <Link
              href={href}
              localePrefix={false}
              title={localeNames[code]}
              aria-current={active ? "true" : undefined}
              className={`transition-colors ${
                active
                  ? "text-foreground"
                  : "text-muted hover:text-foreground"
              }`}
            >
              {localeLabels[code]}
            </Link>
          </span>
        );
      })}
    </div>
  );
}

/** Server-friendly static switcher when pathname is known. */
export function LanguageSwitcherLinks({
  locale,
  pathname,
  className = "",
}: {
  locale: Locale;
  pathname: string;
  className?: string;
}) {
  return (
    <div
      className={`inline-flex items-center gap-0.5 text-xs font-medium tracking-wide ${className}`}
      role="navigation"
      aria-label="Language"
    >
      {locales.map((code, index) => {
        const active = code === locale;
        const href = switchLocalePath(pathname, code);
        return (
          <span key={code} className="inline-flex items-center">
            {index > 0 ? (
              <span className="mx-1.5 text-muted/40" aria-hidden="true">
                /
              </span>
            ) : null}
            <Link
              href={href}
              localePrefix={false}
              title={localeNames[code]}
              aria-current={active ? "true" : undefined}
              className={`transition-colors ${
                active
                  ? "text-foreground"
                  : "text-muted hover:text-foreground"
              }`}
            >
              {localeLabels[code]}
            </Link>
          </span>
        );
      })}
    </div>
  );
}
